import React, { useState, useEffect } from "react";
import { Input, Loading } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { success, error } from "../../lib/userAlerts";
import { secureFetch } from "../../lib/api";
import { TickSquare } from "react-iconly";

const ReceiptArchiveDays = () => {
  const [state, setState] = useState({
    receiptArchiveDays: 0,
    receiptDeleteDays: 0,
    isSaving: {
      archive: false,
      delete: false
    }
  });

  const Access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const fetchReceiptArchiveDaysRange = async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.receiptArchiveDays, {});
      if (ok) {
        setState(prev => ({
          ...prev,
          receiptArchiveDays: json.receiptArchiveDays[0].receipt_archive_days
        }));
      } else {
        error("Oops! error occurred during fetch receipt archive days for this company");
      }
    } catch (err) {
      error("Oops! error occurred during fetch receipt archive days for this company");
      console.error(err);
    }
  };

  const fetchReceiptDeleteDaysRange = async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.fetchReceiptDeleteDays, {});
      if (ok) {
        setState(prev => ({
          ...prev,
          receiptDeleteDays: json.receiptDeleteDays[0].receipt_delete_days
        }));
      } else {
        error("Oops! error occurred during fetch receipt delete days for this company");
      }
    } catch (err) {
      error("Oops! error occurred during fetch receipt delete days for this company");
      console.error(err);
    }
  };

  const saveReceiptArchiveDays = async () => {
    if (state.receiptArchiveDays < 0) {
      error("Please enter a positive value");
      return;
    }

    if (state.receiptDeleteDays < state.receiptArchiveDays) {
      error("Receipt delete days should be greater than the receipt archive days");
      return;
    }

    setState(prev => ({ ...prev, isSaving: { ...prev.isSaving, archive: true } }));
    try {
      const [ok, status, json] = await secureFetch(endpoint.saveReceiptArchiveDays, {
        receiptArchiveDays: state.receiptArchiveDays,
      });
      
      if (ok) {
        success(json.message);
        await fetchReceiptArchiveDaysRange();
      } else {
        error("Oops! error occurred during save receipt archive days for this company");
      }
    } catch (err) {
      error("Oops! error occurred during save receipt archive days for this company");
      console.error(err);
    } finally {
      setState(prev => ({ ...prev, isSaving: { ...prev.isSaving, archive: false } }));
    }
  };

  const saveReceiptDeleteDays = async () => {
    if (state.receiptDeleteDays < 0) {
      error("Please enter a positive value");
      return;
    }

    if (state.receiptDeleteDays < state.receiptArchiveDays) {
      error("Receipt delete days should be greater than the receipt archive days");
      return;
    }

    setState(prev => ({ ...prev, isSaving: { ...prev.isSaving, delete: true } }));
    try {
      const [ok, status, json] = await secureFetch(endpoint.saveReceiptDeleteDays, {
        receiptDeleteDays: state.receiptDeleteDays,
      });
      
      if (ok) {
        success(json.message);
        await fetchReceiptDeleteDaysRange();
      } else {
        error("Oops! error occurred during save receipt delete days for this company");
      }
    } catch (err) {
      error("Oops! error occurred during save receipt delete days for this company");
      console.error(err);
    } finally {
      setState(prev => ({ ...prev, isSaving: { ...prev.isSaving, delete: false } }));
    }
  };

  useEffect(() => {
    fetchReceiptArchiveDaysRange();
    fetchReceiptDeleteDaysRange();
  }, [clientId]);

  if (Access.allow_receipt_management !== 1) {
    return (
      <div role="alert" className="max-w-2xl mx-auto mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
        <div className="flex items-center space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-yellow-800">Please subscribe to this feature</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Archive Days Card */}
        <div className="border-2 border-dotted bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">
              Receipt Archive Days
            </h2>
          </div>

          <div className="flex flex-row justify-between items-center">
            <div className="text-gray-700 font-medium">
              Archive Days
            </div>
            <div className="flex items-center space-x-3">
              <Input
                className="fw6 f4"
                placeholder="0"
                css={{ 
                  $$inputBorderRadius: "8px", 
                  w: 100, 
                  h: 40,
                  mr: 10,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#0070F3"
                  }
                }}
                shadow={false}
                bordered={true}
                rounded={true}
                value={state.receiptArchiveDays}
                onChange={(e) => setState(prev => ({ ...prev, receiptArchiveDays: e.target.value }))}
                disabled={state.isSaving.archive}
              />
              <button
                className={`p-2 rounded-lg transition-all duration-200 ${
                  state.isSaving.archive 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : 'bg-blue-50 hover:bg-blue-100 cursor-pointer'
                }`}
                onClick={state.isSaving.archive ? undefined : saveReceiptArchiveDays}
                disabled={state.isSaving.archive}
              >
                {state.isSaving.archive ? (
                  <Loading size="sm" color="primary" />
                ) : (
                  <TickSquare 
                    size='large' 
                    primaryColor="#0070F3"
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Delete Days Card */}
        <div className="border-2 border-dotted bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">
              Receipt Delete Days
            </h2>
          </div>

          <div className="flex flex-row justify-between items-center">
            <div className="text-gray-700 font-medium">
              Delete Days
            </div>
            <div className="flex items-center space-x-3">
              <Input
                className="fw6 w-100 f4"
                placeholder="0"
                css={{ 
                  $$inputBorderRadius: "8px", 
                  w: 100, 
                  h: 40,
                  mr: 10,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#0070F3"
                  }
                }}
                shadow={false}
                bordered={true}
                rounded={true}
                value={state.receiptDeleteDays}
                onChange={(e) => setState(prev => ({ ...prev, receiptDeleteDays: e.target.value }))}
                disabled={state.isSaving.delete}
              />
              <button
                className={`p-2 rounded-lg transition-all duration-200 ${
                  state.isSaving.delete 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : 'bg-blue-50 hover:bg-blue-100 cursor-pointer'
                }`}
                onClick={state.isSaving.delete ? undefined : saveReceiptDeleteDays}
                disabled={state.isSaving.delete}
              >
                {state.isSaving.delete ? (
                  <Loading size="sm" color="primary" />
                ) : (
                  <TickSquare 
                    size='large' 
                    primaryColor="#0070F3"
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptArchiveDays;